import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import logo from "@images/crjmc-net.svg";
import ButtonCTA from "./ButtonCTA";
import {
  Menu,
  X,
  ChevronDown,
  Shield,
  UserCheck,
  Search,
  HelpCircle,
  BookOpen,
  RefreshCw,
  Building,
  Mail,
  Home,
  Truck,
  Car,
} from "lucide-react";
import BrevoConversationsComponent from "./brevo";
import CookieyesComponent from "./cookieyes";
import Promo from "./Promo";
import StatusSection from "./StatusSection";

const menuItems = {
  services: {
    title: "Services",
    items: [
      { name: "CRJMC in Ontario", path: "/ontario", icon: Shield },
      {
        name: "CRJMC for Real Estate Agents",
        path: "/ontario/real-estate-agents",
        icon: Home,
      },
      {
        name: "CRJMC for Tow Operators",
        path: "/ontario/tow-operators",
        icon: Truck,
      },
      {
        name: "CRJMC for Uber Drivers",
        path: "/ontario/uber",
        icon: Car,
      },
      {
        name: "CRJMC for Security Guards",
        path: "/ontario/security-guards",
        icon: UserCheck,
      },
      {
        name: "CRJMC for Private Investigators",
        path: "/ontario/private-investigators",
        icon: Search,
      },
    ],
  },
  resources: {
    title: "Resources",
    items: [
      { name: "Blog", path: "/posts", icon: BookOpen },
      { name: "FAQ", path: "/faq", icon: HelpCircle },
      { name: "CRJMC Renewal", path: "/renewal-discount", icon: RefreshCw },
    ],
  },
  company: {
    title: "Company",
    items: [
      { name: "About Us", path: "/about", icon: Building },
      { name: "Contact", path: "/contact", icon: Mail },
    ],
  },
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const showStatusSection =
    process.env.GATSBY_FEATURE_STATUS_SECTION === "true";

  const [isWeekend, setIsWeekend] = useState(false);

  useEffect(() => {
    const day = new Date().getDay();
    setIsWeekend(day === 0 || day === 6);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <BrevoConversationsComponent></BrevoConversationsComponent>
      <CookieyesComponent />
      <Promo />
      <header className="bg-black p-3 sm:p-4 relative font-['Raleway',_'Open_Sans',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_'Helvetica_Neue',_Arial,_sans-serif]">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 sm:gap-4 z-20 flex-wrap">
            <Link
              to="/"
              className="text-white text-xl sm:text-2xl font-bold flex items-center gap-2"
            >
              <img src={logo} alt="CRJMC Logo" className="h-5 sm:h-7" />
              <span>CRJMC.net</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex space-x-6">
              {Object.entries(menuItems).map(([key, section]) => (
                <div key={key} className="relative group">
                  <button className="text-white hover:text-emerald-400 py-2 flex items-center gap-1 transition-colors">
                    {section.title}
                    <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </button>

                  <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl py-3 z-50 transition-all duration-200 transform origin-top-left scale-95 group-hover:scale-100">
                    <div className="relative">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="block px-4 py-2.5 text-gray-800 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
                          >
                            <Icon className="h-5 w-5 text-emerald-600" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </nav>
            <ButtonCTA
              showSubheader={false}
              className="bg-emerald-700 text-white text-sm px-4 py-2 rounded-md font-medium hover:bg-emerald-800 transition-colors"
              buttonText="Get Started"
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white z-[60]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="fixed inset-0 bg-black z-50 md:hidden overflow-y-auto w-full">
              <div className="flex flex-col min-h-screen pt-20 px-6 pb-6">
                <div className="space-y-6 w-full">
                  {Object.entries(menuItems).map(([key, section]) => (
                    <div key={key} className="space-y-2">
                      <h3 className="text-emerald-400 font-medium text-lg">
                        {section.title}
                      </h3>
                      <div className="space-y-1 pl-2">
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="block py-2.5 text-white hover:text-emerald-400 flex items-center gap-3 transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <Icon className="h-5 w-5" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <ButtonCTA
                    showSubheader={false}
                    className="w-full bg-emerald-700 text-white text-base px-6 py-3 rounded hover:bg-emerald-800 transition-colors"
                    buttonText="Get Started"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
      {!isWeekend && <StatusSection />}
    </>
  );
};

export default Header;
